import { useEffect, useState } from 'react';

import { locationSchema } from './utils';
import styles from '../styles.module.css';
import { UpdateLocation } from './types';
import { AdminCardForm } from '@/components/AdminToolComponents/card';
import DateInput from '@/components/Form/DateInput';
import Input from '@/components/Form/Input';
import InputSearch from '@/components/Form/InputSearch';
import { SearchResult } from '@/components/Form/InputSearch/types';
import RadioButton from '@/components/Form/RadioButton';
import useSearchCompanies from '@/hooks/services/Companies';

export const statusContainer = ({
  showEditTurbine = true,
  status,
  handleChangeFormData = () => {}
}: {
  showEditTurbine?: boolean;
  status: 'Active' | 'Inactive';
  handleChangeFormData: (field: any, value: any) => void;
}) => {
  const radioOptions = {
    active: 'Active',
    inactive: 'Inactive'
  };

  return showEditTurbine ? (
    <>
      <RadioButton
        checked={status === radioOptions.active}
        onChange={() => handleChangeFormData('status', radioOptions.active)}
        label={radioOptions.active}
      />
      <RadioButton
        checked={status === radioOptions.inactive}
        onChange={() => handleChangeFormData('status', radioOptions.inactive)}
        label={radioOptions.inactive}
      />
    </>
  ) : (
    <>
      <span className={styles.adminText}>Status</span>
      <span className={styles.adminBlueText}>{status}</span>
    </>
  );
};

export const EditLocationForm = ({
  formData,
  handleChangeFormData,
  handleSelectFormData,
  showEditTurbine = true,
  formErrors = {}
}: {
  formData: UpdateLocation;
  handleChangeFormData: (field: keyof UpdateLocation, value: any) => void;
  handleSelectFormData: (value: any) => void;
  showEditTurbine?: boolean;
  formErrors?: { [k: string]: string };
}) => {
  const requiredFields = Object.keys(locationSchema.shape);
  const [searchCompanyInput, setSearchCompanyInput] = useState('');
  const [companyResults, setCompanyResults] = useState<SearchResult[]>([]);

  const { data: dataCompanies, isFetching: isFetchingCompanies } =
    useSearchCompanies(searchCompanyInput, {
      useQueryOptions: {
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: searchCompanyInput.length > 0
      }
    });

  useEffect(() => {
    const companiesResults = dataCompanies?.data || [];
    setCompanyResults(companiesResults);
  }, [dataCompanies]);

  const onSearch = (query: string) => {
    if (query) {
      setSearchCompanyInput(query);
    } else {
      setCompanyResults([]);
    }
  };

  const onSelect = (item: SearchResult) => {
    handleSelectFormData([
      { field: 'company', value: item.name },
      { field: 'companyId', value: item.id }
    ]);
    setCompanyResults([]);
  };

  const onReset = () => {
    handleSelectFormData([
      { field: 'company', value: '' },
      { field: 'companyId', value: '' }
    ]);
    setSearchCompanyInput('');
    setCompanyResults([]);
  };

  const inputComponent = ({
    id,
    label,
    readOnly = false,
    extraProps
  }: {
    id: keyof UpdateLocation;
    label: string;
    readOnly?: boolean;
    extraProps?: any;
  }) => (
    <Input
      label={label}
      value={formData[id] as string}
      onChange={(value: string) => handleChangeFormData(id, value)}
      required={requiredFields.includes(id as string)}
      readOnly={readOnly}
      error={formErrors[id as string] || undefined}
      customClass={styles.adminInput}
      {...extraProps}
    />
  );

  return (
    <AdminCardForm>
      {inputComponent({ id: 'id', label: 'ID', readOnly: true })}

      {inputComponent({
        id: 'city',
        label: 'Nearest City',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'name',
        label: 'Name',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'state',
        label: 'State',
        readOnly: !showEditTurbine
      })}

      <InputSearch
        label="Customer"
        placeHolder="Search customer"
        defaultValue={formData?.customer}
        results={companyResults}
        process={isFetchingCompanies}
        onSearch={onSearch}
        onSelect={onSelect}
        onReset={onReset}
        required
        readOnly={!showEditTurbine}
        error={formErrors['customer'] || undefined}
      />

      {inputComponent({
        id: 'latitude',
        label: 'Latitude',
        readOnly: !showEditTurbine
      })}

      <DateInput
        id="startDate"
        label="Start Date"
        value={formData.startDate || ''}
        onChange={value => handleChangeFormData('startDate', value)}
        readOnly={!showEditTurbine}
        disabled={!showEditTurbine}
        error={!!formErrors.startDate}
      />

      {inputComponent({
        id: 'longitude',
        label: 'Longitude',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'address',
        label: 'Address',
        readOnly: !showEditTurbine
      })}

      {inputComponent({
        id: 'numberOfTurbines',
        label: 'Number of Turbines',
        readOnly: true
      })}
    </AdminCardForm>
  );
};
